import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Clapperboard, Film, Upload, Sparkles, Link as LinkIcon, Youtube,
  Wand2, Images, Ticket, Plus, Trash2, Save, Clapperboard as StudioIcon, Download, DollarSign,
  Tv, MonitorPlay,
} from "lucide-react";
import { sr } from "../../lib/sr";

const EMPTY = {
  title: "", imdb_url: "", website: "", trailer_url: "", genre: "", rating: "", release_date: "",
  runtime: "", tagline: "", synopsis: "",
  release_type: "theatrical", platform: "", watch_url: "", streaming_objective: "tune_in",
  brand_objective: "awareness", slate: "",
};

const RELEASE_TYPES = [
  { k: "theatrical", l: "Theatrical", ic: Ticket },
  { k: "streaming", l: "Streaming", ic: MonitorPlay },
  { k: "hybrid", l: "Hybrid", ic: Tv },
];
const STREAMING_OBJECTIVES = [
  { k: "tune_in", l: "Tune-in / views" }, { k: "rental", l: "Rentals" },
  { k: "purchase", l: "Purchases" }, { k: "subscription", l: "Subscriptions" },
];
const BRAND_OBJECTIVES = [
  { k: "awareness", l: "Build awareness" }, { k: "slate_following", l: "Grow following" },
  { k: "catalog_streaming", l: "Drive catalog streaming" },
];

// Movie-distributor capture: Film Sales Model (brand vs. a specific film) + release-type
// branch (Theatrical / Streaming / Hybrid) that tailors the fields, goals and the AI plan.
// Bubbles { mode, itemId, fields } up via onState so onboarding can pick release-aware goals.
export default function MoviePromoCapture({ initial = {}, onState, compact = false }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initial.mode || "movie");
  const [f, setF] = useState({ ...EMPTY, ...(initial.fields || {}) });
  const [itemId, setItemId] = useState(initial.itemId || null);
  const [posterUrl, setPosterUrl] = useState(initial.posterUrl || "");
  const [adCreatives, setAdCreatives] = useState([]);
  const [showtimes, setShowtimes] = useState(initial.showtimes || [{ market: "", url: "" }]);
  const [budgetTotal, setBudgetTotal] = useState("");
  const [split, setSplit] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState({});
  const fileRef = useRef(null);
  const fRef = useRef(f); fRef.current = f;
  const idRef = useRef(itemId); idRef.current = itemId;
  const modeRef = useRef(mode); modeRef.current = mode;

  const releaseType = f.release_type || "theatrical";
  const streaming = releaseType === "streaming" || releaseType === "hybrid";
  const theatrical = releaseType !== "streaming"; // theatrical | hybrid show showtimes

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const b = (k, v) => setBusy((p) => ({ ...p, [k]: v }));

  useEffect(() => { onState?.({ mode, itemId, fields: f, posterUrl }); }, [mode, itemId, f, posterUrl]); // eslint-disable-line

  const ensureItem = async () => {
    const cur = { ...fRef.current, sales_model: modeRef.current };
    if (idRef.current) { await sr.saveMoviePromo({ ...cur, item_id: idRef.current }).catch(() => {}); return idRef.current; }
    if (!cur.title.trim()) { toast.error(modeRef.current === "brand" ? "Enter your studio / brand name first" : "Enter the movie title first"); return null; }
    const r = await sr.saveMoviePromo(cur);
    setItemId(r.item.id); idRef.current = r.item.id;
    return r.item.id;
  };

  const autofill = async () => {
    if (!f.imdb_url.trim() && !f.trailer_url.trim()) { toast.error("Paste an IMDB or YouTube link first"); return; }
    b("auto", true);
    try {
      const r = await sr.movieMetadata({ imdb_url: f.imdb_url || undefined, trailer_url: f.trailer_url || undefined });
      const nf = r.fields || {};
      setF((p) => ({
        ...p,
        title: p.title || nf.title || "", genre: p.genre || nf.genre || "", rating: p.rating || nf.rating || "",
        release_date: p.release_date || nf.release_date || "", runtime: nf.runtime || p.runtime || "",
        synopsis: p.synopsis || nf.synopsis || "", trailer_url: p.trailer_url || nf.trailer_url || "",
      }));
      (r.notes || []).forEach((n) => toast.message(n));
      if (nf.poster_url && !posterUrl) {
        const id = await ensureItem();
        if (id) { const pr = await sr.posterFromUrl(id, nf.poster_url); setPosterUrl(pr.poster_url); }
      }
      if (r.ok) toast.success(`Pulled details from ${(r.sources || []).join(" + ")}`);
    } catch (e) { toast.error("Auto-fill failed — enter details manually"); }
    finally { b("auto", false); }
  };

  const onPoster = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!f.title.trim()) { toast.error("Enter the movie title first"); if (fileRef.current) fileRef.current.value = ""; return; }
    b("poster", true);
    try {
      const id = await ensureItem(); if (!id) return;
      const r = await sr.uploadMoviePoster(id, file);
      setPosterUrl(r.poster_url); setAdCreatives([]);
      toast.success("Poster uploaded — it's now the movie's creative");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not upload the poster"); }
    finally { b("poster", false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const makeAds = async () => {
    b("ads", true);
    try {
      const id = await ensureItem(); if (!id) return;
      const r = await sr.posterAds(id);
      setAdCreatives(r.creatives || []);
      toast.success("Ad sizes ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create ad sizes"); }
    finally { b("ads", false); }
  };

  const recommend = async () => {
    if (!f.title.trim()) { toast.error(mode === "brand" ? "Enter your studio / brand name first" : "Enter the movie title first"); return; }
    b("plan", true);
    try { const r = await sr.movieRecommend({ ...f, sales_model: mode }); setPlan(r.plan); ensureItem(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not build a plan"); }
    finally { b("plan", false); }
  };

  const saveShowtimes = async () => {
    const rows = showtimes.filter((s) => s.market.trim() && s.url.trim());
    b("show", true);
    try { const id = await ensureItem(); if (!id) return; await sr.saveShowtimes(id, rows); toast.success(`Saved ${rows.length} ticketing link(s)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save links"); }
    finally { b("show", false); }
  };

  const doSplit = async () => {
    const t = parseFloat(budgetTotal);
    if (!t || t <= 0) { toast.error("Enter a total monthly budget"); return; }
    b("split", true);
    try { const id = await ensureItem(); if (!id) return; const r = await sr.budgetSplit(id, t); setSplit(r.allocations); toast.success("Budget split across your markets"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Add markets with showtime links first"); }
    finally { b("split", false); }
  };

  const savePlanToMovie = async () => {
    b("saveplan", true);
    try { const id = await ensureItem(); if (!id) return; await sr.savePromoPlan(id, plan); toast.success("Plan saved to the movie"); }
    catch (e) { toast.error("Could not save the plan"); }
    finally { b("saveplan", false); }
  };

  const openStudio = async () => {
    const id = await ensureItem();
    if (id) navigate(`/app/movie-studio/${id}`);
  };

  return (
    <div className="sr-field" data-testid="movie-promo-capture" style={{ marginTop: 10, padding: 14, borderRadius: 12, background: "#F7FAFF", border: "1px solid #DCE6F5" }}>
      <label className="sr-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Clapperboard size={15} /> Are you promoting your brand, or a specific movie?
      </label>
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        {[{ k: "brand", l: "My brand / studio" }, { k: "movie", l: "A specific movie" }].map((o) => {
          const on = mode === o.k;
          return (
            <button key={o.k} type="button" onClick={() => setMode(o.k)} data-testid={`movie-promo-mode-${o.k}`}
              style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              {on ? "✓ " : ""}{o.l}
            </button>
          );
        })}
      </div>

      {/* ---------------- Brand / studio slate mode ---------------- */}
      {mode === "brand" && (
        <div style={{ marginTop: 14 }} data-testid="movie-promo-brand-fields">
          <div className="sr-field"><label className="sr-label">What's the goal for your brand?</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              {BRAND_OBJECTIVES.map((o) => {
                const on = f.brand_objective === o.k;
                return (
                  <button key={o.k} type="button" onClick={() => setF((p) => ({ ...p, brand_objective: o.k }))} data-testid={`brand-objective-${o.k}`}
                    style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                    {on ? "✓ " : ""}{o.l}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="sr-field"><label className="sr-label">Studio / distributor name</label>
            <input className="sr-input" value={f.title} onChange={set("title")} placeholder="e.g. Harborlight Pictures" data-testid="brand-name" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="sr-field"><label className="sr-label">Website</label>
              <input className="sr-input" value={f.website} onChange={set("website")} placeholder="https://yourstudio.com" data-testid="brand-website" /></div>
            <div className="sr-field"><label className="sr-label"><Youtube size={12} /> Slate sizzle / trailer</label>
              <input className="sr-input" value={f.trailer_url} onChange={set("trailer_url")} placeholder="https://youtube.com/watch?v=…" data-testid="brand-trailer" /></div>
          </div>
          <div className="sr-field"><label className="sr-label">What are you promoting across your slate?</label>
            <textarea className="sr-input" rows={3} value={f.slate} onChange={set("slate")} data-testid="brand-slate"
              placeholder="e.g. Three upcoming thrillers and our back-catalog streaming on Prime & Apple TV." /></div>
          {!compact && (
            <>
              <button type="button" className="sr-btn sr-btn-primary sr-btn-sm" onClick={recommend} disabled={busy.plan} data-testid="brand-recommend" style={{ marginTop: 4 }}>
                {busy.plan ? <><Loader2 size={13} className="sr-spin" /> Building plan…</> : <><Sparkles size={13} /> Recommend how to promote your brand</>}
              </button>
              {plan && (
                <>
                  <MoviePlan plan={plan} />
                  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <button type="button" className="sr-btn sr-btn-sm sr-btn-primary" onClick={savePlanToMovie} disabled={busy.saveplan} data-testid="brand-save-plan">
                      {busy.saveplan ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save plan</>}
                    </button>
                    <button type="button" className="sr-btn sr-btn-sm" onClick={openStudio} data-testid="brand-open-studio"><StudioIcon size={13} /> Open Marketing Studio</button>
                  </div>
                </>
              )}
            </>
          )}
          {compact && (
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }} data-testid="movie-promo-compact-note">
              You'll build the brand plan, creatives and budget in the Movie Studio right after setup.
            </p>
          )}
        </div>
      )}

      {/* ---------------- Specific movie mode ---------------- */}
      {mode === "movie" && (
        <div style={{ marginTop: 14 }} data-testid="movie-promo-fields">
          {/* Release type branch */}
          <div className="sr-field" data-testid="movie-release-type">
            <label className="sr-label">How is this movie releasing?</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              {RELEASE_TYPES.map((o) => {
                const on = releaseType === o.k; const Ic = o.ic;
                return (
                  <button key={o.k} type="button" onClick={() => setF((p) => ({ ...p, release_type: o.k }))} data-testid={`movie-release-type-${o.k}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    <Ic size={14} />{o.l}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="sr-field"><label className="sr-label"><LinkIcon size={11} /> IMDB link</label>
              <input className="sr-input" value={f.imdb_url} onChange={set("imdb_url")} placeholder="https://imdb.com/title/tt…" data-testid="movie-promo-imdb" /></div>
            <div className="sr-field"><label className="sr-label"><Youtube size={12} /> YouTube trailer URL</label>
              <input className="sr-input" value={f.trailer_url} onChange={set("trailer_url")} placeholder="https://youtube.com/watch?v=…" data-testid="movie-promo-trailer" /></div>
          </div>
          <button type="button" className="sr-btn sr-btn-sm" onClick={autofill} disabled={busy.auto} data-testid="movie-promo-autofill" style={{ marginBottom: 10 }}>
            {busy.auto ? <Loader2 size={13} className="sr-spin" /> : <><Wand2 size={13} /> Auto-fill from links</>}
          </button>

          <div className="sr-field"><label className="sr-label">Movie title</label>
            <input className="sr-input" value={f.title} onChange={set("title")} placeholder="e.g. Midnight in Mumbai" data-testid="movie-promo-title" /></div>
          <div className="sr-field"><label className="sr-label">Movie website</label>
            <input className="sr-input" value={f.website} onChange={set("website")} placeholder="https://yourmovie.com" data-testid="movie-promo-website" /></div>
          <div className="sr-field"><label className="sr-label">Tagline <span className="sr-muted" style={{ fontWeight: 400 }}>· shown on the ad creative</span></label>
            <input className="sr-input" value={f.tagline} onChange={set("tagline")} placeholder="e.g. Every secret has a price." data-testid="movie-promo-tagline" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
            <div className="sr-field"><label className="sr-label">Genre</label>
              <input className="sr-input" value={f.genre} onChange={set("genre")} placeholder="Thriller" data-testid="movie-promo-genre" /></div>
            <div className="sr-field"><label className="sr-label">Rating</label>
              <input className="sr-input" value={f.rating} onChange={set("rating")} placeholder="PG-13" data-testid="movie-promo-rating" /></div>
            <div className="sr-field"><label className="sr-label">Runtime</label>
              <input className="sr-input" value={f.runtime} onChange={set("runtime")} placeholder="118 min" data-testid="movie-promo-runtime" /></div>
            <div className="sr-field"><label className="sr-label">{streaming && releaseType === "streaming" ? "Launch date" : "Release date"}</label>
              <input className="sr-input" value={f.release_date} onChange={set("release_date")} placeholder="2026-08-15" data-testid="movie-promo-release" /></div>
          </div>

          {/* Streaming / hybrid destination fields */}
          {streaming && (
            <div data-testid="movie-streaming-fields" style={{ padding: 12, borderRadius: 10, background: "#fff", border: "1px solid #DCE6F5", marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}><MonitorPlay size={14} /> Streaming details</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="sr-field"><label className="sr-label">Platform</label>
                  <input className="sr-input" value={f.platform} onChange={set("platform")} placeholder="e.g. Netflix, Prime Video" data-testid="movie-platform" /></div>
                <div className="sr-field"><label className="sr-label">Watch / app link</label>
                  <input className="sr-input" value={f.watch_url} onChange={set("watch_url")} placeholder="https://watch…" data-testid="movie-watch-url" /></div>
              </div>
              <div className="sr-field"><label className="sr-label">Streaming objective</label>
                <select className="sr-select" value={f.streaming_objective} onChange={set("streaming_objective")} data-testid="movie-streaming-objective">
                  {STREAMING_OBJECTIVES.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
                </select></div>
            </div>
          )}

          {compact && posterUrl && (
            <div className="sr-field" data-testid="movie-promo-compact-poster" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
              <img src={posterUrl} alt="poster" data-testid="movie-promo-poster-preview" style={{ height: 72, borderRadius: 8, border: "1px solid var(--border)" }} />
              <span className="sr-muted" style={{ fontSize: 12 }}>Poster pulled from your link — it'll be this movie's ad creative.</span>
            </div>
          )}

          {/* Poster upload + poster-to-ads */}
          {!compact && (<>
          <div className="sr-field">
            <label className="sr-label">Movie poster <span className="sr-muted" style={{ fontWeight: 400 }}>· used as the ad creative</span></label>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/*" onChange={onPoster} style={{ display: "none" }} data-testid="movie-promo-poster-input" />
              <button type="button" className="sr-btn sr-btn-sm" onClick={() => fileRef.current?.click()} disabled={busy.poster} data-testid="movie-promo-poster-upload">
                {busy.poster ? <Loader2 size={13} className="sr-spin" /> : <><Upload size={13} /> {posterUrl ? "Replace poster" : "Upload poster"}</>}
              </button>
              {posterUrl
                ? <img src={posterUrl} alt="poster" data-testid="movie-promo-poster-preview" style={{ height: 76, borderRadius: 8, border: "1px solid var(--border)" }} />
                : <span style={{ height: 76, width: 54, borderRadius: 8, background: "#0F172A", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Film size={20} style={{ color: "#64748B" }} /></span>}
              {posterUrl && (
                <button type="button" className="sr-btn sr-btn-sm sr-btn-primary" onClick={makeAds} disabled={busy.ads} data-testid="movie-promo-make-ads">
                  {busy.ads ? <Loader2 size={13} className="sr-spin" /> : <><Images size={13} /> Create ad sizes</>}
                </button>
              )}
            </div>
            {adCreatives.length > 0 && (
              <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap" }} data-testid="movie-promo-ad-creatives">
                {adCreatives.map((c) => (
                  <a key={c.key} href={c.url} download target="_blank" rel="noreferrer" data-testid={`movie-ad-${c.key}`}
                    style={{ textAlign: "center", textDecoration: "none" }}>
                    <img src={c.url} alt={c.label} style={{ height: 110, borderRadius: 8, border: "1px solid var(--border)", background: "#0F172A" }} />
                    <div className="sr-muted" style={{ fontSize: 11, marginTop: 3, display: "inline-flex", alignItems: "center", gap: 3 }}><Download size={10} /> {c.label}</div>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Per-market showtime / ticketing links — theatrical & hybrid only */}
          {theatrical && (
          <div className="sr-field" data-testid="movie-showtimes-block">
            <label className="sr-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Ticket size={13} /> Showtime / ticketing links <span className="sr-muted" style={{ fontWeight: 400 }}>· per market</span></label>
            {showtimes.map((row, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }} data-testid={`showtime-row-${i}`}>
                <input className="sr-input" style={{ flex: "0 0 32%" }} placeholder="Market (e.g. New York)" value={row.market}
                  onChange={(e) => setShowtimes((p) => p.map((r, j) => (j === i ? { ...r, market: e.target.value } : r)))} data-testid={`showtime-market-${i}`} />
                <input className="sr-input" style={{ flex: 1 }} placeholder="https://tickets…/buy" value={row.url}
                  onChange={(e) => setShowtimes((p) => p.map((r, j) => (j === i ? { ...r, url: e.target.value } : r)))} data-testid={`showtime-url-${i}`} />
                <button type="button" className="sr-btn sr-btn-sm sr-btn-ghost" onClick={() => setShowtimes((p) => p.filter((_, j) => j !== i))} data-testid={`showtime-remove-${i}`}><Trash2 size={13} /></button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="sr-btn sr-btn-sm sr-btn-ghost" onClick={() => setShowtimes((p) => [...p, { market: "", url: "" }])} data-testid="showtime-add"><Plus size={13} /> Add market</button>
              <button type="button" className="sr-btn sr-btn-sm" onClick={saveShowtimes} disabled={busy.show} data-testid="showtime-save">
                {busy.show ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save links</>}
              </button>
            </div>
          </div>
          )}

          {/* Smart budget split across markets */}
          <div className="sr-field">
            <label className="sr-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><DollarSign size={13} /> Smart budget split <span className="sr-muted" style={{ fontWeight: 400 }}>· auto-divided across your markets</span></label>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="sr-input" type="number" min="0" placeholder="Total monthly budget ($)" value={budgetTotal} onChange={(e) => setBudgetTotal(e.target.value)} data-testid="budget-split-total" style={{ flex: 1 }} />
              <button type="button" className="sr-btn sr-btn-sm" onClick={doSplit} disabled={busy.split} data-testid="budget-split-run">
                {busy.split ? <Loader2 size={13} className="sr-spin" /> : <>Split across markets</>}
              </button>
            </div>
            {split && (
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }} data-testid="budget-split-result">
                {split.map((a) => (
                  <div key={a.market} className="sr-badge sr-badge-gray" style={{ fontSize: 12.5 }} data-testid={`budget-alloc-${a.market}`}>
                    {a.market}: <strong style={{ color: "var(--navy)" }}>${Number(a.amount).toLocaleString()}/mo</strong>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="button" className="sr-btn sr-btn-primary sr-btn-sm" onClick={recommend} disabled={busy.plan} data-testid="movie-promo-recommend" style={{ marginTop: 4 }}>
            {busy.plan ? <><Loader2 size={13} className="sr-spin" /> Building plan…</> : <><Sparkles size={13} /> Recommend how to promote this movie</>}
          </button>

          {plan && (
            <>
              <MoviePlan plan={plan} />
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <button type="button" className="sr-btn sr-btn-sm sr-btn-primary" onClick={savePlanToMovie} disabled={busy.saveplan} data-testid="movie-promo-save-plan">
                  {busy.saveplan ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save plan to movie</>}
                </button>
                <button type="button" className="sr-btn sr-btn-sm" onClick={openStudio} data-testid="movie-promo-open-studio">
                  <StudioIcon size={13} /> Open Marketing Studio
                </button>
              </div>
            </>
          )}
          </>)}
          {compact && (
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }} data-testid="movie-promo-compact-note">
              You'll add the poster, showtime links, budget split and AI promotion plan in the Movie Studio right after setup — no need to re-enter this movie.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function MoviePlan({ plan }) {
  return (
    <div className="sr-card" style={{ marginTop: 12, background: "#fff" }} data-testid="movie-promo-plan">
      <strong style={{ color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center" }}><Sparkles size={15} color="var(--blue)" /> Your movie promotion plan</strong>
      <p style={{ color: "var(--navy)", fontSize: 13.5, marginTop: 8 }}>{plan.summary}</p>
      {plan.positioning && <p className="sr-muted" style={{ fontSize: 13, marginTop: -2 }}><em>{plan.positioning}</em></p>}
      <Section title="Recommended channels" testid="movie-plan-channels">
        {(plan.channels || []).map((c, i) => {
          const dooh = /dooh|out-of-home/i.test(c.name || "");
          return (
            <div key={i} style={{ marginBottom: 7 }} data-testid={dooh ? "movie-plan-dooh" : undefined}>
              <strong style={{ color: "var(--navy)", fontSize: 13 }}>{c.name}{c.budget_share ? ` · ${c.budget_share}` : ""}</strong>
              {dooh && <span className="sr-badge sr-badge-gold" style={{ marginLeft: 6, fontSize: 10.5 }}>DOOH via your ad platform</span>}
              <div className="sr-muted" style={{ fontSize: 12.5 }}>{c.why}</div>
              {c.estimated_views && <div style={{ fontSize: 12, color: "var(--blue)", fontWeight: 600 }}>Est. views: {c.estimated_views}</div>}
            </div>
          );
        })}
      </Section>
      <Section title="Who to target" testid="movie-plan-audiences">
        <ul style={{ margin: 0, paddingLeft: 18, color: "var(--navy)", fontSize: 13 }}>{(plan.audiences || []).map((a, i) => <li key={i}>{a}</li>)}</ul>
      </Section>
      <Section title="Launch timeline" testid="movie-plan-timeline">
        {(plan.timeline || []).map((t, i) => (
          <div key={i} style={{ fontSize: 13, color: "var(--navy)", marginBottom: 3 }}><strong>{t.phase}:</strong> {t.action}</div>
        ))}
      </Section>
      {(plan.social_angles || []).length > 0 && (
        <Section title="Social angles" testid="movie-plan-social">
          <ul style={{ margin: 0, paddingLeft: 18, color: "var(--navy)", fontSize: 13 }}>{plan.social_angles.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </Section>
      )}
      {(plan.poster_tips || []).length > 0 && (
        <Section title="Poster / creative tips" testid="movie-plan-poster-tips">
          <ul style={{ margin: 0, paddingLeft: 18, color: "var(--navy)", fontSize: 13 }}>{plan.poster_tips.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </Section>
      )}
    </div>
  );
}

const Section = ({ title, children, testid }) => (
  <div style={{ marginTop: 12 }} data-testid={testid}>
    <div className="sr-eyebrow" style={{ marginBottom: 6 }}>{title}</div>
    {children}
  </div>
);
